import { Controller } from '@hotwired/stimulus';
import TomSelect from 'tom-select';

export default class extends Controller {
  static values = {
    options: Array,
    disableSearch: Boolean,
    removableOption: { type: Boolean, default: true },
    // allows the dropdown to open upwards when there is not enough room below
    dropup: { type: Boolean, default: false },
  };

  connect () {
    if (!this.element.tomselect) {
      (() => new TomSelect(this.element, this._settingsFactory(this.element)))();
    }
  }

  disconnect () {
    const value = this.element.value;

    this.element.tomselect.destroy();

    // necessary to persist value because
    // this.element.tomselect.destroy() sets the value to null
    this.element.value = value;
  }

  submitForm() {
    this.element.form.requestSubmit();
  }

  _toggleDropup (dropdown) {
    const { wrapper, control } = this.element.tomselect;
    const controlRect = control.getBoundingClientRect();
    const spaceBelow = window.innerHeight - controlRect.bottom;
    const spaceAbove = controlRect.top;
    const needsDropup = (
      dropdown.offsetHeight > spaceBelow && spaceAbove > spaceBelow
    );

    wrapper.classList.toggle('ts-dropup', needsDropup);
  }

  _settingsFactory (element) {
    const ajaxUrl = element.dataset.ajaxUrl;
    const ajaxTextField = element.dataset.ajaxTextField;
    const ajaxValueField = element.dataset.ajaxValueField;
    const ajaxMatcher = element.dataset.ajaxSearchMatcher;
    const ajaxDisplayedField = (
      element.dataset.ajaxDisplayedField ? element.dataset.ajaxDisplayedField : ajaxTextField
    );
    const ajaxAdditionalFilters = (
      element.dataset.ajaxAdditionalFilters
        ? JSON.parse(element.dataset.ajaxAdditionalFilters)
        : []
    );

    const additionalFiltersQuery = ajaxAdditionalFilters.reduce((acc, filter) => {
      return acc + `&q[${filter[0]}]=${filter[1]}`;
    }, '');

    const formatAjaxData = (json, valueField, textField) => {
      return json.map(entry => (
        {
          value: entry[valueField],
          text: entry[textField]
        }
      ));
    };

    const settingsStore = {
      create: {
        create: input => ({ value: '', text: input }),
        persist: false
      },
      ajax: {
        valueField: 'value',
        labelField: 'text',
        searchField: 'text',
        load: function (query, callback) {
          const searchQuery = `q[${ajaxTextField}_${ajaxMatcher}]=${encodeURIComponent(query)}`;
          const url = `${ajaxUrl}?${searchQuery}${additionalFiltersQuery}`;
          fetch(url, {
            headers: {
              Accept: 'application/json'
            }
          })
            .then(response => response.json())
            .then(json => {
              callback(formatAjaxData(json, ajaxValueField, ajaxDisplayedField));
            }).catch((e) => {
              callback();
            });
        }
      }
    };

    const placeholder = element.dataset.placeholder || '';

    const settings = {
      placeholder: placeholder,
      maxOptions: null,
      onItemAdd: function () {
        this.setTextboxValue('');
      },
      plugins: this.removableOptionValue ? { remove_button: { title: 'remove' } } : {},
    };

    if (this.disableSearchValue) {
      settings.controlInput = null;
      delete settings.plugins.remove_button;
    }

    if (this.dropupValue) {
      settings.onDropdownOpen = dropdown => this._toggleDropup(dropdown);
      settings.onDropdownClose = () => (
        this.element.tomselect.wrapper.classList.remove('ts-dropup')
      );
    }

    if (this.hasOptionsValue) { Object.assign(settings, settings, {options: this.optionsValue}); }
    if (element.classList.contains('tom-select-ajax')) { Object.assign(settings, settings, settingsStore.ajax); }
    if (element.classList.contains('tom-select-create')) { Object.assign(settings, settings, settingsStore.create); }

    return settings;
  }
}
